import { RequirementCoverage, VerificationOutcome } from "../api/cppmodelApi";

// SDK 0.11.0 stored rule outcomes as parameters named CppModel.Verify.<name>.
export const VERIFY_PREFIX = "CppModel.Verify.";

// A set of time intervals drawn over the 2D graph.
export interface GraphHighlight {
  id: string;
  label: string;
  kind: "violation" | "coverage";
  // [from, to] in seconds; null ends reach the start or end of the run.
  intervals: [number | null, number | null][];
}

// A time range (s) the 2D graph should zoom to; a new nonce zooms again to
// the same range.
export interface FocusRequest {
  range: [number, number];
  nonce: number;
}

export const ruleHighlightId = (name: string) => `rule:${name}`;
export const requirementHighlightId = (id: string) => `requirement:${id}`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toNumberOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// The server stores outcomes as submitted, so read them defensively.
const parseOutcome = (value: Record<string, unknown>): VerificationOutcome => {
  const status = value.status === "pass" || value.status === "fail" || value.status === "error"
    ? value.status
    : value.pass === true ? "pass" : "fail";

  return {
    kind: typeof value.kind === "string" ? value.kind : "unknown",
    status,
    pass: status === "pass",
    margins: isObject(value.margins)
      ? Object.fromEntries(Object.entries(value.margins).map(([name, margin]) => [name, toNumberOrNull(margin)]))
      : {},
    metrics: isObject(value.metrics) ? value.metrics : {},
    violations: Array.isArray(value.violations)
      ? value.violations
        .filter((interval): interval is unknown[] => Array.isArray(interval) && interval.length === 2)
        .map(([from, to]) => [toNumberOrNull(from), toNumberOrNull(to)] as [number | null, number | null])
      : [],
    explanation: typeof value.explanation === "string" ? value.explanation : "",
  };
};

// Rule name -> outcome from the execution's verification field, or for
// executions of SDK 0.11.0 from its CppModel.Verify.<name> parameters.
// Undefined when the run has no rules.
export const parseVerification = (
  verification: unknown,
  parameters: unknown,
): Record<string, VerificationOutcome> | undefined => {
  const entries = isObject(verification)
    ? Object.entries(verification)
    : isObject(parameters)
      ? Object.entries(parameters)
        .filter(([name]) => name.startsWith(VERIFY_PREFIX))
        .map(([name, value]) => [name.slice(VERIFY_PREFIX.length), value] as const)
      : [];
  const outcomes = entries
    .filter((entry): entry is [string, Record<string, unknown>] => isObject(entry[1]))
    .map(([name, value]) => [name, parseOutcome(value)] as const);

  return outcomes.length > 0 ? Object.fromEntries(outcomes) : undefined;
};

// Requirement ID -> coverage, or undefined when the run named none.
export const parseRequirements = (requirements: unknown): Record<string, RequirementCoverage> | undefined => {
  if (!isObject(requirements)) {
    return undefined;
  }

  const coverage = Object.entries(requirements)
    .filter((entry): entry is [string, Record<string, unknown>] => isObject(entry[1]))
    .map(([id, value]) => [id, {
      whole_run: value.whole_run === true,
      steps: typeof value.steps === "number" ? value.steps : 0,
      from_s: toNumberOrNull(value.from_s),
      to_s: toNumberOrNull(value.to_s),
    }] as const);

  return coverage.length > 0 ? Object.fromEntries(coverage) : undefined;
};

// The interval a requirement covers, or null when it was only named at no
// time at all (which the API does not produce).
export const getCoverageInterval = (coverage: RequirementCoverage): [number | null, number | null] | null => {
  if (coverage.whole_run) {
    return [null, null];
  }

  return coverage.steps > 0 ? [coverage.from_s, coverage.to_s] : null;
};

export const describeCoverage = (coverage: RequirementCoverage): string => {
  const steps = coverage.steps > 0
    ? `named at ${coverage.steps} step${coverage.steps === 1 ? "" : "s"}, t = ${formatSeconds(coverage.from_s)} – ${formatSeconds(coverage.to_s)} s`
    : "";

  if (coverage.whole_run) {
    return steps ? `Whole run (also ${steps})` : "Whole run";
  }

  return steps ? steps.charAt(0).toUpperCase() + steps.slice(1) : "Not covered at any step";
};

export const formatSeconds = (value: number | null) => value === null ? "∞" : `${Number(value.toPrecision(6))}`;

// Rules that failed first, then errors, then passes; by name within each.
export const sortOutcomes = (verification: Record<string, VerificationOutcome>) => {
  const order = { fail: 0, error: 1, pass: 2 };

  return Object.entries(verification).sort(([nameA, a], [nameB, b]) => (
    order[a.status] - order[b.status] || nameA.localeCompare(nameB)
  ));
};

// A range around an interval with some room on both sides, clamped to the run.
export const getFocusRange = (
  [from, to]: [number | null, number | null],
  runEnd: number,
): [number, number] => {
  const start = from ?? 0;
  const end = to ?? runEnd;
  const padding = Math.max((end - start) * 0.25, runEnd * 0.01, 1e-3);

  return [Math.max(0, start - padding), Math.min(Math.max(runEnd, end), end + padding)];
};
